/**
 * Dashboard customization page.
 * Allows users to create/manage dashboard views and arrange widgets via drag-and-drop grid layout.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, Save as SaveIcon } from '@mui/icons-material';
import { type Layout, type LayoutItem, useContainerWidth } from 'react-grid-layout';
import { ResponsiveReactGridLayout } from 'react-grid-layout/legacy';

import { dashboardService } from '../services/dashboardService';
import type {
  CreateDashboardViewRequest,
  CreateDashboardWidgetRequest,
  DashboardWidgetDto,
  UpdateDashboardViewRequest,
  UpdateDashboardWidgetRequest,
  WidgetLayoutUpdate,
} from '../types/dashboard';
import { WidgetPalette, type WidgetTemplate } from '../components/dashboard/WidgetPalette';
import { WidgetEditorDialog } from '../components/dashboard/WidgetEditorDialog';

const safeParseJson = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
};

/**
 * Dashboard customization page.
 */
export const DashboardCustomizationPage = () => {
  const queryClient = useQueryClient();
  const { width, containerRef } = useContainerWidth();

  const [selectedViewId, setSelectedViewId] = useState<string>('');
  const [createName, setCreateName] = useState('My Dashboard');
  const [createDescription, setCreateDescription] = useState('');
  const [createIsDefault, setCreateIsDefault] = useState(true);
  const [createIsShared, setCreateIsShared] = useState(false);

  const [layout, setLayout] = useState<Layout>([]);
  const [editingWidget, setEditingWidget] = useState<DashboardWidgetDto | null>(null);

  const reorderDebounceHandle = useRef<number | null>(null);

  const viewsQuery = useQuery({
    queryKey: ['dashboardViews'],
    queryFn: () => dashboardService.getDashboardViews(),
  });

  const selectedViewQuery = useQuery({
    queryKey: ['dashboardView', selectedViewId],
    enabled: !!selectedViewId,
    queryFn: () => dashboardService.getDashboardViewById(selectedViewId),
  });

  const selectedView = selectedViewQuery.data ?? null;

  // Initialize selection to first view (prefer default).
  useEffect(() => {
    const views = viewsQuery.data ?? [];
    if (views.length === 0) return;
    if (selectedViewId) return;

    const defaultView = views.find((v) => v.isDefault) ?? views[0];
    setSelectedViewId(defaultView.id);
  }, [selectedViewId, viewsQuery.data]);

  // Derive layout from widgets when a view is loaded/refreshed.
  useEffect(() => {
    if (!selectedView) return;
    const widgets = selectedView.widgets ?? [];
    setLayout(
      widgets.map((w) => ({
        i: w.id,
        x: w.positionX ?? 0,
        y: w.positionY ?? 0,
        w: Math.max(1, w.width ?? 1),
        h: Math.max(1, w.height ?? 1),
      })),
    );
  }, [selectedView?.id, selectedView?.updatedAt]);

  const createViewMutation = useMutation({
    mutationFn: async (req: CreateDashboardViewRequest) => dashboardService.createDashboardView(req),
    onSuccess: async (view) => {
      await queryClient.invalidateQueries({ queryKey: ['dashboardViews'] });
      setSelectedViewId(view.id);
    },
  });

  const updateViewMutation = useMutation({
    mutationFn: async ({ id, req }: { id: string; req: UpdateDashboardViewRequest }) =>
      dashboardService.updateDashboardView(id, req),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dashboardViews'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboardView', selectedViewId] });
    },
  });

  const deleteViewMutation = useMutation({
    mutationFn: async (id: string) => dashboardService.deleteDashboardView(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dashboardViews'] });
      setSelectedViewId('');
    },
  });

  const createWidgetMutation = useMutation({
    mutationFn: async (req: CreateDashboardWidgetRequest) => dashboardService.createDashboardWidget(req),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dashboardView', selectedViewId] });
    },
  });

  const updateWidgetMutation = useMutation({
    mutationFn: async ({ id, req }: { id: string; req: UpdateDashboardWidgetRequest }) =>
      dashboardService.updateDashboardWidget(id, req),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dashboardView', selectedViewId] });
    },
  });

  const deleteWidgetMutation = useMutation({
    mutationFn: async (id: string) => dashboardService.deleteDashboardWidget(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dashboardView', selectedViewId] });
    },
  });

  const reorderWidgetsMutation = useMutation({
    mutationFn: async ({ dashboardViewId, updates }: { dashboardViewId: string; updates: WidgetLayoutUpdate[] }) =>
      dashboardService.reorderWidgets(dashboardViewId, updates),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dashboardView', selectedViewId] });
    },
  });

  const viewOptions = useMemo(() => viewsQuery.data ?? [], [viewsQuery.data]);

  const renderWidgetBody = (w: DashboardWidgetDto) => {
    const config = safeParseJson(w.configurationJson ?? '{}');
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          <strong>Type:</strong> {w.widgetType}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          <strong>Config:</strong> {config ? JSON.stringify(config) : w.configurationJson}
        </Typography>
      </Box>
    );
  };

  const scheduleReorder = (dashboardViewId: string, nextLayout: Layout) => {
    if (reorderDebounceHandle.current) {
      window.clearTimeout(reorderDebounceHandle.current);
    }

    reorderDebounceHandle.current = window.setTimeout(() => {
      const ordered = [...nextLayout].sort((a: LayoutItem, b: LayoutItem) => a.y - b.y || a.x - b.x || a.i.localeCompare(b.i));
      const updates: WidgetLayoutUpdate[] = ordered.map((l, index) => ({
        widgetId: l.i,
        sortOrder: index,
        positionX: l.x,
        positionY: l.y,
        width: l.w,
        height: l.h,
      }));

      reorderWidgetsMutation.mutate({ dashboardViewId, updates });
    }, 400);
  };

  const handleAddWidget = (template: WidgetTemplate) => {
    if (!selectedView) return;

    const existing = selectedView.widgets ?? [];
    const nextSortOrder = existing.length === 0 ? 0 : Math.max(...existing.map((w) => w.sortOrder ?? 0)) + 1;

    const req: CreateDashboardWidgetRequest = {
      dashboardViewId: selectedView.id,
      widgetType: template.widgetType,
      title: template.title,
      description: template.description,
      configurationJson: template.configurationJson,
      positionX: 0,
      positionY: 0,
      width: template.defaultWidth,
      height: template.defaultHeight,
      isVisible: true,
      sortOrder: nextSortOrder,
    };

    createWidgetMutation.mutate(req);
  };

  const handleSaveViewLayoutJson = async () => {
    if (!selectedView) return;

    const req: UpdateDashboardViewRequest = {
      name: selectedView.name,
      description: selectedView.description,
      isDefault: selectedView.isDefault,
      isShared: selectedView.isShared,
      layoutJson: JSON.stringify(layout),
    };

    await updateViewMutation.mutateAsync({ id: selectedView.id, req });
  };

  const handleUpdateWidget = async (widgetId: string, req: UpdateDashboardWidgetRequest) => {
    await updateWidgetMutation.mutateAsync({ id: widgetId, req });
  };

  const isBusy =
    viewsQuery.isLoading ||
    selectedViewQuery.isLoading ||
    createViewMutation.isPending ||
    updateViewMutation.isPending ||
    deleteViewMutation.isPending ||
    createWidgetMutation.isPending ||
    updateWidgetMutation.isPending ||
    deleteWidgetMutation.isPending ||
    reorderWidgetsMutation.isPending;

  return (
    <Box>
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" component="h1" fontWeight={600} gutterBottom>
          Dashboard Customization
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create dashboard views and arrange widgets with drag-and-drop.
        </Typography>
      </Box>

      {(viewsQuery.isError || selectedViewQuery.isError) && (
        <Alert severity="error" sx={{ mb: 4 }}>
          Failed to load dashboard views. Ensure the backend API is running and you have selected a tenant/project.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left column: view management + palette */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Views
              </Typography>

              <TextField
                select
                fullWidth
                label="Selected view"
                value={selectedViewId}
                onChange={(e) => setSelectedViewId(e.target.value)}
                disabled={viewOptions.length === 0}
              >
                {viewOptions.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.name} {v.isDefault ? '(Default)' : ''}
                  </MenuItem>
                ))}
              </TextField>

              {selectedView && (
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={`Widgets: ${selectedView.widgetCount ?? (selectedView.widgets?.length ?? 0)}`} />
                  <Chip label={`Updated: ${formatDateTime(selectedView.updatedAt)}`} variant="outlined" />
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Create new view
              </Typography>
              <Box sx={{ display: 'grid', gap: 2 }}>
                <TextField label="Name" value={createName} onChange={(e) => setCreateName(e.target.value)} />
                <TextField
                  label="Description"
                  value={createDescription}
                  onChange={(e) => setCreateDescription(e.target.value)}
                  multiline
                  minRows={2}
                />
                <TextField
                  select
                  label="Default"
                  value={createIsDefault ? 'yes' : 'no'}
                  onChange={(e) => setCreateIsDefault(e.target.value === 'yes')}
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </TextField>
                <TextField
                  select
                  label="Shared"
                  value={createIsShared ? 'yes' : 'no'}
                  onChange={(e) => setCreateIsShared(e.target.value === 'yes')}
                >
                  <MenuItem value="no">No</MenuItem>
                  <MenuItem value="yes">Yes</MenuItem>
                </TextField>

                <Button
                  variant="contained"
                  disabled={!createName.trim() || isBusy}
                  onClick={() =>
                    createViewMutation.mutate({
                      name: createName.trim(),
                      description: createDescription.trim() || undefined,
                      isDefault: createIsDefault,
                      isShared: createIsShared,
                      layoutJson: '{}',
                    })
                  }
                >
                  Create View
                </Button>
              </Box>

              {selectedView && (
                <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    disabled={isBusy}
                    onClick={() => {
                      const confirmed = window.confirm(`Delete dashboard view "${selectedView.name}"? This cannot be undone.`);
                      if (!confirmed) return;
                      deleteViewMutation.mutate(selectedView.id);
                    }}
                  >
                    Delete View
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<SaveIcon />}
                    disabled={isBusy}
                    onClick={handleSaveViewLayoutJson}
                  >
                    Save Layout JSON
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <WidgetPalette onAdd={handleAddWidget} />
            </CardContent>
          </Card>
        </Grid>

        {/* Right column: layout canvas */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Canvas
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Drag widgets to reposition, drag handles to resize. Changes are auto-saved (debounced) to the backend reorder endpoint.
              </Typography>

              {!selectedView && (
                <Alert severity="info">
                  Create a view (or select an existing one) to start customizing.
                </Alert>
              )}

              {selectedView && (
                <Box ref={containerRef} sx={{ backgroundColor: 'action.hover', borderRadius: 2, p: 1 }}>
                  <ResponsiveReactGridLayout
                    width={width}
                    className="layout"
                    layouts={{ lg: layout }}
                    breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                    cols={{ lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 }}
                    rowHeight={30}
                    compactType="vertical"
                    isResizable
                    isDraggable
                    onLayoutChange={(next: Layout) => {
                      setLayout(next);
                      scheduleReorder(selectedView.id, next);
                    }}
                  >
                    {(selectedView.widgets ?? []).map((w) => (
                      <div key={w.id}>
                        <Card sx={{ height: '100%' }}>
                          <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle1" fontWeight={600} noWrap>
                                  {w.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {w.widgetType}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<EditIcon />}
                                  onClick={() => setEditingWidget(w)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={<DeleteIcon />}
                                  onClick={() => {
                                    const confirmed = window.confirm(`Delete widget "${w.title}"?`);
                                    if (!confirmed) return;
                                    deleteWidgetMutation.mutate(w.id);
                                  }}
                                >
                                  Delete
                                </Button>
                              </Box>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ flex: 1, overflow: 'auto' }}>{renderWidgetBody(w)}</Box>
                          </CardContent>
                        </Card>
                      </div>
                    ))}
                  </ResponsiveReactGridLayout>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <WidgetEditorDialog open={!!editingWidget} widget={editingWidget} onClose={() => setEditingWidget(null)} onSave={handleUpdateWidget} />
    </Box>
  );
};

